import { useState } from 'react';
import { GridCellParams, GridColDef } from '@mui/x-data-grid-pro';
import { HiddenAssetSource, HiddenRecord, InventoryAsset } from '../../../../../types';
import { useInventory } from '../../../../../store/Inventory';
import { BasicDataGrid } from '../../../../Compounds/Table/BasicDataGrid';
import { DetailViewCard } from '../../../../Compounds/CardWithTitle/DetailViewCard';
import { DetailCTARenderer } from '../../../../Compounds/UpstackDataGrid/CellRenderers/DetailCTARenderer';
import { HideConfirmationDialog, HideRecordButton } from '../../../../Compounds/HideConfirmationDialog';
import { OrderDetailsCTARenderer } from '../../../../Compounds/UpstackDataGrid/CellRenderers/OrderDetailsCTARenderer';
import { termLabel, usaCurrency } from '../../../../../utils/helpers';
import { Permission } from '../../../../../store/GeneralStore';
import { PermissibleRender } from '../../../../Compounds/PermissibleRender';

export interface HiddenAssetsTableProps {
  accountId: string;
  hiddenAssets: HiddenRecord[];
}

interface SelectedAsset {
  id: string;
  source?: HiddenAssetSource;
  type?: InventoryAsset['type'];
}

const HiddenAssetsTable = ({ accountId, hiddenAssets }: HiddenAssetsTableProps) => {
  const [hideConfirmationModalOpen, setHideConfirmationModalOpen] = useState(false);
  const [selected, setSelected] = useState<SelectedAsset | null>(null);
  const { deleteHiddenAsset } = useInventory();

  const handleUnhide = async () => {
    if (selected) {
      setHideConfirmationModalOpen(false);
      // Pass the row's source so only the matching surface's row is unhidden — an
      // asset hidden from both Inventory and Benchmark keeps its other hide intact.
      await deleteHiddenAsset(selected.id, accountId, selected.source);
      setSelected(null);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'details',
      headerName: 'Details',
      sortable: false,
      width: 65,
      filterable: false,
      type: 'singleSelect',
      valueOptions: [],
      renderCell: (params: GridCellParams) => {
        // A hidden project has no detail to open: its page is built from a list it is no longer in.
        if (params.row.type === 'Project') return null;
        // A quote has no page of its own; the order request it belongs to is where it is shown.
        if (params.row.type === 'Quote') {
          const orderId = (params.row as HiddenRecord).orderRequest?.sfId;
          return orderId ? <OrderDetailsCTARenderer orderId={orderId} /> : null;
        }
        return (
          <DetailCTARenderer
            data={params}
            value={''}
            valueFormatted={''}
          />
        );
      },
      hideable: false,
      disableReorder: true
    },
    {
      headerName: '',
      field: 'actions',
      sortable: false,
      filterable: false,
      width: 65,
      renderCell: (params) => (
        <PermissibleRender requiredPermissions={[Permission.EDIT_HIDDEN_INVENTORY]}>
          <HideRecordButton
            hidden
            testId="unhideButton"
            onClick={() => {
              // Legacy Inventory hides have no source; default to 'Inventory' so the
              // unhide DELETE always targets the correct record.
              setSelected({ id: params.row.id, source: params.row.source ?? 'Inventory', type: params.row.type });
              setHideConfirmationModalOpen(true);
            }}
          />
        </PermissibleRender>
      )
    },
    {
      field: 'source',
      headerName: 'Source',
      sortable: true,
      width: 110,
      filterable: false,
      valueGetter: (_value, row) => row?.source ?? 'Inventory',
      type: 'singleSelect',
      valueOptions: ['Inventory', 'Benchmark', 'Project', 'Quote'],
      description: 'Drag to move column'
    },
    {
      field: 'type',
      headerName: 'Type',
      sortable: true,
      width: 100,
      filterable: false,
      valueGetter: (_value, row) => row?.type ?? 'Inventory',
      type: 'singleSelect',
      valueOptions: ['Inventory', 'Project', 'Quote'],
      description: 'Drag to move column'
    },
    {
      field: 'address',
      headerName: 'Address',
      sortable: true,
      width: 100,
      filterable: false,
      type: 'singleSelect',
      valueOptions: [],
      description: 'Drag to move column'
    },
    {
      field: 'productDisplay',
      headerName: 'Product',
      width: 100,
      filterable: false,
      type: 'singleSelect',
      valueOptions: [],
      description: 'Drag to move column'
    },
    {
      field: 'supplier.name',
      headerName: 'Supplier',
      valueGetter: (value, row) => row?.supplier?.name,
      sortable: true,
      width: 100,
      filterable: false,
      type: 'singleSelect',
      valueOptions: [],
      description: 'Drag to move column'
    },
    {
      field: 'orderRequest',
      headerName: 'Order Request',
      valueGetter: (_value, row: HiddenRecord) => row?.orderRequest?.name || row?.orderRequest?.requestNumber,
      sortable: true,
      width: 140,
      filterable: false,
      type: 'singleSelect',
      valueOptions: [],
      description: 'Drag to move column'
    },
    {
      field: 'mRC',
      headerName: 'Monthly Cost',
      sortable: true,
      valueFormatter: (value) => (value == null ? '' : usaCurrency.format(value)),
      valueGetter: (value) => (value == null ? null : Number(value)),
      width: 100,
      filterable: false,
      type: 'singleSelect',
      valueOptions: [],
      description: 'Drag to move column'
    },
    {
      field: 'term',
      headerName: 'Term Length',
      sortable: true,
      valueFormatter: (value: string) => termLabel(value),
      width: 100,
      filterable: false,
      type: 'singleSelect',
      valueOptions: [],
      description: 'Drag to move column'
    }
  ];

  return (
    <DetailViewCard
      title="Hidden Records"
      enableCollapse
      collapsed>
      <BasicDataGrid
        rows={hiddenAssets}
        columns={columns}
        // '|' is the separator DetailCTARenderer strips to hand the drawer the bare record id.
        getRowId={(row) => `${row.id}|${row.source ?? 'Inventory'}`}
        className="h-96 overflow-auto"
      />
      <HideConfirmationDialog
        open={hideConfirmationModalOpen}
        onClose={() => {
          setHideConfirmationModalOpen(false);
          setSelected(null);
        }}
        onConfirm={() => handleUnhide()}
        type="Unhide"
        recordType={selected?.type ?? 'Inventory'}
      />
    </DetailViewCard>
  );
};

export default HiddenAssetsTable;
